<template>
  <transition name="drawer">
    <div
      v-if="isApplicantDrawer && apiApplicantDrawer"
      class="drawer-twitter-applicant"
      data-drawer-not-close
      v-loading="applicantDrawerLoading"
    >
      <div class="drawer-twitter-applicant-header">
        <Account
          :name="apiApplicantDrawer.name"
          :image="apiApplicantDrawer.picture_url"
          :is-display-name="false"
          is-hide-symbol
          symbol="twitter"
        />

        <div>
          <div class="drawer-applicant-name">{{ apiApplicantDrawer.name }}</div>
          <a class="drawer-screen-name" :href="`https://x.com/${apiApplicantDrawer.screen_name}`" target="_blank">
            @{{ apiApplicantDrawer.screen_name }}
          </a>
        </div>

        <el-tooltip :enterable="false" v-if="apiApplicantDrawer.is_follower" placement="top" content="フォロー中">
          <Icon name="check-circle" size="small" color="success" />
        </el-tooltip>

        <el-tooltip :enterable="false" v-if="apiApplicantDrawer.is_suspended_account" placement="top" content="凍結中">
          <Icon name="snowflake-alert" size="small" color="info" />
        </el-tooltip>

        <el-tooltip :enterable="false" v-if="apiApplicantDrawer.is_positive" placement="top" content="ポジティブ判定">
          <Icon name="heart" size="small" color="primary" />
        </el-tooltip>

        <el-tooltip :enterable="false" v-if="apiApplicantDrawer.is_warning" placement="top" content="ネガティブ判定">
          <Icon name="alert" size="small" color="warning" />
        </el-tooltip>

        <el-tooltip :enterable="false" v-if="apiApplicantDrawer.is_ng" placement="top" content="NGアカウント">
          <Icon name="cancel" size="small" color="danger" />
        </el-tooltip>

        <el-tooltip
          :enterable="false"
          v-if="apiApplicantDrawer.is_follower && apiApplicantDrawer.is_new_follower"
          placement="top"
          content="新規獲得フォロワー"
        >
          <Icon name="new-box" size="small" color="danger" />
        </el-tooltip>

        <div class="drawer-close" @click="clickCloseApplicantDrawer">
          <Icon name="close" size="small" />
        </div>
      </div>

      <el-tabs :value="applicantDrawerTab" @tab-click="changeTab">
        <el-tab-pane label="プロフィール" name="profile">
          <div class="drawer-profile-header">
            <div>
              <span>フォロー</span>
              <span class="drawer-profile-data">{{ apiApplicantDrawer.follows_count.toLocaleString() }}</span>
            </div>
            <div>
              <span>フォロワー</span>
              <span class="drawer-profile-data">{{ apiApplicantDrawer.followers_count.toLocaleString() }}</span>
            </div>
            <div>
              <span>アカウント開設日</span>
              <span class="drawer-profile-data">
                {{ getTimeFormat(apiApplicantDrawer.join_datetime, 'll') }}
              </span>
            </div>
          </div>

          <div v-if="apiApplicantDrawer.description" class="drawer-profile-description">
            <AppTextHighlight
              :text="apiApplicantDrawer.description"
              :positiveWords="apiApplicantDrawer.positive_keywords"
              :negativeWords="apiApplicantDrawer.negative_keywords"
            />
          </div>

          <div class="drawer-profile-type">
            <el-radio v-model="profile_type" border label="post" @change="handleRadioChange('post')">
              応募ポスト({{ apiApplicantDrawer.posts.length }})
            </el-radio>

            <el-tooltip
              :enterable="false"
              content="ブラウザ版Xにログインしていると、タイムラインを表示できます。"
              placement="top"
            >
              <el-radio v-model="profile_type" border label="timeline" @change="handleRadioChange('timeline')">
                最新のポスト
              </el-radio>
            </el-tooltip>
          </div>

          <div ref="postEmbed" :style="style_tweets_scroll">
            <template v-if="profile_type === 'post'">
              <div v-for="post in apiApplicantDrawer.posts" :key="post.id">
                <div class="drawer-post-publish-date">{{ getTimeFormat(post.publish_datetime) }}</div>
                <TwitterPostEmbed :id="post.id" />
              </div>
            </template>
            <template v-if="profile_type === 'timeline'">
              <div :key="apiApplicantDrawer.screen_name">
                <TwitterTimelineWidget :id="apiApplicantDrawer.screen_name" tweet-limit="20" />
              </div>
            </template>
          </div>
        </el-tab-pane>
        <el-tab-pane :label="`参加履歴(${apiApplicantDrawer.entry_histories.length})`" name="entryHistory">
          <ApplicantHistoryTable
            :histories="apiApplicantDrawer.entry_histories"
            :maxHeight="table_height"
            type="entryHistory"
          />
        </el-tab-pane>
        <el-tab-pane :label="`当選履歴(${apiApplicantDrawer.win_histories.length})`" name="winHistory">
          <ApplicantHistoryTable
            :histories="apiApplicantDrawer.win_histories"
            :maxHeight="table_height"
            type="winHistory"
          />
        </el-tab-pane>
        <el-tab-pane :label="`配信履歴(${apiApplicantDrawer.stream_histories.length})`" name="streamHistory">
          <div :style="style_stream_scroll">
            <div class="drawer-stream-item" v-for="stream in apiApplicantDrawer.stream_histories" :key="stream.id">
              <ApplicantStreamItem :stream="stream" />
            </div>
            <div v-if="!apiApplicantDrawer.stream_histories.length" class="drawer-stream-item drawer-stream-empty">
              データがありません
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
      <div ref="footer" class="drawer-twitter-applicant-footer">
        <div v-if="applicantDrawerTab === 'profile'" class="drawer-profile-action">
          <ButtonIcon
            :disabled="is_candidate_disabled"
            :tooltip="candidate_button_tooltip"
            :type="candidate_button_type"
            @click="updateCandidate()"
            name="star"
          />
          <ButtonIcon
            :disabled="is_winner_disabled"
            :tooltip="winner_button_tooltip"
            :type="winner_button_type"
            @click="updateWinner()"
            name="trophy"
          />
          <div class="drawer-responder">
            <ButtonIcon
              :disabled="is_responder_disabled"
              :tooltip="responder_button_tooltip"
              :type="responder_button_type"
              @click="updateResponder()"
              name="clipboard-check"
            />
          </div>

          <div class="drawer-ng">
            <ButtonIcon :type="ng_button_type" name="cancel" :tooltip="ng_button_tooltip" @click="updateNgAccount()" />
          </div>
        </div>

        <el-pagination
          @prev-click="prevApplicantDrawer()"
          @next-click="nextApplicantDrawer()"
          :page-size="1"
          :current-page="current_page"
          :total="total"
          layout="prev, slot, next"
        >
          <span class="drawer-current-page">
            {{ current_page }}
          </span>
          <span> / </span>
          <span>
            {{ total }}
          </span>
        </el-pagination>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import { AppLabel, AppTextHighlight } from '@/client/components/_atoms'
import Icon from '@/client/components/_atoms/Icon.vue'
import TwitterPostEmbed from '@/client/components/_atoms/twitter_post_embed'
import TwitterTimelineWidget from '@/client/components/_atoms/twitter_timeline_widget'
import { ApplicantHistoryTable, ApplicantStreamItem } from '@/client/components/_molecules'
import Account from '@/client/components/_molecules/Account.vue'
import ButtonIcon from '@/client/components/_molecules/ButtonIcon.vue'
import { TrackingService } from '@/client/services'
import { useTwitterApplicantStore } from '@/client/store/useTwitterApplicantStore'
import { getTimeFormat as f } from '@/client/utils/filters'

const ENTRY_HISTORY_EXCEPT_TABLE_HEIGHT = 256
const STREAM_HISTORY_EXCEPT_STREAM_HEIGHT = 240

export default defineComponent({
  components: {
    Icon,
    Account,
    AppLabel,
    AppTextHighlight,
    ButtonIcon,
    ApplicantHistoryTable,
    ApplicantStreamItem,
    TwitterPostEmbed,
    TwitterTimelineWidget
  },
  props: {
    campaignType: String
  },
  data() {
    return {
      getTimeFormat: f,
      profile_type: 'post',
      tweets_height: 0,
      table_height: window.innerHeight - ENTRY_HISTORY_EXCEPT_TABLE_HEIGHT,
      stream_height: window.innerHeight - STREAM_HISTORY_EXCEPT_STREAM_HEIGHT
    }
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('click', this.closeDrawer)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('click', this.closeDrawer)
  },
  watch: {
    drawerApplicantId: {
      immediate: true,
      async handler() {
        await this.$nextTick()

        // UI描画前だと、正しい高さを取得できないため、1秒遅延させる
        window.setTimeout(() => this.getScrollHeight(), 1000)
      }
    },
    async applicantDrawerTab() {
      await this.$nextTick()
      this.getScrollHeight()
    }
  },
  computed: {
    ...mapState(useTwitterApplicantStore, [
      'applicants',
      'total',
      'params',
      'isApplicantDrawer',
      'applicantDrawerLoading',
      'drawerApplicantId',
      'apiApplicantDrawer',
      'applicantDrawerTab',
      'isDmSending'
    ]),
    is_candidate_disabled() {
      return this.apiApplicantDrawer && !this.apiApplicantDrawer.is_candidate && this.apiApplicantDrawer.is_ng
    },
    candidate_button_type() {
      return this.apiApplicantDrawer && this.apiApplicantDrawer.is_candidate ? 'primary' : 'default'
    },
    candidate_button_tooltip() {
      if (this.apiApplicantDrawer && this.apiApplicantDrawer.is_candidate) return '候補者を解除'

      return this.apiApplicantDrawer && this.apiApplicantDrawer.is_ng ? 'NGアカウント' : '候補者に追加'
    },
    is_winner_disabled() {
      if (this.isDmSending) return true

      return (
        (this.apiApplicantDrawer && !this.apiApplicantDrawer.is_winner && this.apiApplicantDrawer.is_ng) ||
        this.campaignType === 'old_web_instantwin'
      )
    },
    winner_button_type() {
      return this.apiApplicantDrawer && this.apiApplicantDrawer.is_winner ? 'primary' : 'default'
    },
    winner_button_tooltip() {
      if (this.isDmSending) return 'DM送信中のため変更できません'

      if (this.apiApplicantDrawer && this.apiApplicantDrawer.is_winner) return '当選者を解除'

      return this.apiApplicantDrawer && this.apiApplicantDrawer.is_ng ? 'NGアカウント' : '当選者に追加'
    },
    is_responder_disabled() {
      return this.apiApplicantDrawer && !this.apiApplicantDrawer.is_responder && this.apiApplicantDrawer.is_ng
    },
    responder_button_type() {
      return this.apiApplicantDrawer && this.apiApplicantDrawer.is_responder ? 'primary' : 'default'
    },
    responder_button_tooltip() {
      if (this.apiApplicantDrawer && this.apiApplicantDrawer.is_responder) return '回答者を解除'

      return this.apiApplicantDrawer && this.apiApplicantDrawer.is_ng ? 'NGアカウント' : '回答者に追加'
    },
    ng_button_type() {
      return this.apiApplicantDrawer && this.apiApplicantDrawer.is_ng ? 'primary' : 'default'
    },
    ng_button_tooltip() {
      return this.apiApplicantDrawer && this.apiApplicantDrawer.is_ng ? 'NGアカウントを解除' : 'NGアカウントに追加'
    },
    current_page() {
      const prev_applicant_count = (this.params.pagingNo - 1) * this.params.limitCount

      const applicant_ids = this.applicants.map(v => v.id)

      const index = applicant_ids.indexOf(this.drawerApplicantId)

      return prev_applicant_count + index + 1
    },
    style_tweets_scroll() {
      return { height: `${this.tweets_height}px`, overflowY: 'auto' }
    },
    style_stream_scroll() {
      return { height: `${this.stream_height}px`, overflowY: 'auto' }
    }
  },
  methods: {
    ...mapActions(useTwitterApplicantStore, [
      'closeApplicantDrawer',
      'changeApplicantDrawerTab',
      'updateApplicant',
      'prevApplicantDrawer',
      'nextApplicantDrawer'
    ]),
    /**
     * スクロールの高さを取得する
     */
    getScrollHeight() {
      if (!this.$refs.postEmbed || !this.$refs.footer) return

      const postEmbed = this.$refs.postEmbed.getBoundingClientRect()

      const footer = this.$refs.footer.getBoundingClientRect()

      this.tweets_height = footer.top - postEmbed.top
    },
    /**
     * タブを変更する
     */
    async changeTab(event) {
      switch (event.name) {
        case 'profile':
          TrackingService.sendEvent('click:応募一覧|ユーザー詳細|プロフィール')
          break
        case 'entryHistory':
          TrackingService.sendEvent('click:応募一覧|ユーザー詳細|参加履歴')
          break
        case 'winHistory':
          TrackingService.sendEvent('click:応募一覧|ユーザー詳細|当選履歴')
          break
        case 'streamHistory':
          TrackingService.sendEvent('click:応募一覧|ユーザー詳細|配信履歴')
          break
        default:
          break
      }

      await this.changeApplicantDrawerTab(event.name)
    },
    /**
     * 候補者フラグを更新する
     */
    async updateCandidate() {
      TrackingService.sendEvent('click:ユーザー詳細|プロフィール|候補者に追加')

      if (!this.drawerApplicantId || !this.apiApplicantDrawer) return

      const option = {
        applicantId: this.drawerApplicantId,
        isCandidate: !this.apiApplicantDrawer.is_candidate
      }

      const result = await this.updateApplicant(option)

      if (!result.data) {
        this.$notify({
          title: '候補者の保存に失敗しました',
          message: '',
          customClass: 'danger'
        })

        return
      }

      const applicant_ids = this.applicants.map(v => v.id)

      if (!applicant_ids.includes(this.drawerApplicantId)) {
        await this.closeApplicantDrawer()
      }
    },
    /**
     * 当選者フラグを更新する
     */
    async updateWinner() {
      TrackingService.sendEvent('click:ユーザー詳細|プロフィール|当選者に追加')

      if (!this.drawerApplicantId || !this.apiApplicantDrawer) return

      const option: any = {
        applicantId: this.drawerApplicantId,
        isWinner: !this.apiApplicantDrawer.is_winner
      }

      if (option.isWinner) {
        option.wasWinner = true
      }

      const result = await this.updateApplicant(option)

      if (result.error && result.error.type === 'MAX_WINNER_COUNT_OVER') {
        this.$notify({
          title: '残り当選数に空きがありません。',
          message: '',
          customClass: 'danger'
        })

        return
      }

      if (result.error && result.error.type === 'DM_SENDING_IN_PROGRESS') {
        this.$notify({
          title: 'DM送信中のため変更できません。',
          message: 'DM送信が終了してから再度お試しください。',
          customClass: 'danger'
        })

        return
      }

      if (!result.data) {
        this.$notify({
          title: '当選者の保存に失敗しました',
          message: '',
          customClass: 'danger'
        })

        return
      }

      const applicant_ids = this.applicants.map(v => v.id)

      if (!applicant_ids.includes(this.drawerApplicantId)) {
        await this.closeApplicantDrawer()
      }
    },
    /**
     * 回答者フラグを更新する
     */
    async updateResponder() {
      TrackingService.sendEvent('click:ユーザー詳細|プロフィール|回答者に追加')

      if (!this.drawerApplicantId || !this.apiApplicantDrawer) return

      const option = {
        applicantId: this.drawerApplicantId,
        isResponder: !this.apiApplicantDrawer.is_responder
      }

      const result = await this.updateApplicant(option)

      if (!result.data) {
        this.$notify({
          title: '回答者の保存に失敗しました',
          message: '',
          customClass: 'danger'
        })

        return
      }

      const applicant_ids = this.applicants.map(v => v.id)

      if (!applicant_ids.includes(this.drawerApplicantId)) {
        await this.closeApplicantDrawer()
      }
    },
    /**
     * NGアカウントを更新する
     */
    async updateNgAccount() {
      TrackingService.sendEvent('click:ユーザー詳細|プロフィール|NGアカウントに追加')

      if (!this.drawerApplicantId || !this.apiApplicantDrawer) return

      if (!this.apiApplicantDrawer.is_ng) {
        const confirm = window.confirm(
          'NGアカウントに追加すると、この応募者は抽選対象から除外されます。よろしいですか？'
        )

        if (!confirm) return
      }

      const option = {
        applicantId: this.drawerApplicantId,
        isNg: !this.apiApplicantDrawer.is_ng
      }

      const result = await this.updateApplicant(option)

      if (!result.data) {
        return this.$notify({
          title: 'NGアカウントの更新に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
      }

      const applicant_ids = this.applicants.map(v => v.id)

      if (!applicant_ids.includes(this.drawerApplicantId)) {
        await this.closeApplicantDrawer()
      }
    },
    /**
     * windowリサイズ時の高さ検知
     */
    handleResize() {
      this.getScrollHeight()
      this.table_height = window.innerHeight - ENTRY_HISTORY_EXCEPT_TABLE_HEIGHT
      this.stream_height = window.innerHeight - STREAM_HISTORY_EXCEPT_STREAM_HEIGHT
    },
    /**
     * ドロワー関連以外の場所をクリックした場合にドロワーを閉じる
     */
    async closeDrawer(event) {
      const nodeList = document.querySelectorAll('[data-drawer-not-close]')

      let isDrawerElement = false
      for (const node of nodeList) {
        isDrawerElement = node.contains(event.target)

        if (isDrawerElement) break
      }

      if (!isDrawerElement) {
        await this.closeApplicantDrawer()
      }
    },
    handleRadioChange(value) {
      TrackingService.sendEvent(`radio:ユーザー詳細|プロフィール|${value === 'post' ? '応募ポスト' : '最新のポスト'}`)
    },
    async clickCloseApplicantDrawer() {
      TrackingService.sendEvent('click:応募一覧|ユーザー詳細|閉じる')

      await this.closeApplicantDrawer()
    }
  }
})
</script>
<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

$drawer-width: 460px;

.drawer-enter-active,
.drawer-leave-active {
  transform: translateX(0);
  transition: all 0.3s ease-in-out;
}

.drawer-enter,
.drawer-leave-to {
  transform: translateX(100%);
}

.drawer-twitter-applicant {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: $spacing-unit;
  width: $drawer-width;
  padding: $spacing-unit * 2;
  background: white;
  box-shadow: -$spacing-unit 0 $spacing-unit 0 $shadow-color;
}

.drawer-twitter-applicant-header {
  display: flex;
  gap: $spacing-unit;
  align-items: center;
}

.drawer-applicant-name {
  height: 16px;
}

.drawer-screen-name {
  font-size: 12px;
  color: $sub-text-color;
}

.drawer-close {
  display: inline-block;
  margin-left: auto;
  cursor: pointer;

  .mdi {
    line-height: $spacing-unit * 2;
  }
}

.drawer-profile-header {
  display: flex;
  gap: $spacing-unit;
  align-items: center;
}

.drawer-profile-data {
  display: inline-block;
  font-weight: bold;
}

.drawer-profile-description {
  margin: $spacing-unit 0;
}

.drawer-profile-type {
  margin: $spacing-unit * 2 0;
}

.drawer-profile-action {
  display: flex;
  gap: $spacing-unit * 2;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: $spacing-unit;
  background: $background-color;
}

.drawer-responder {
  margin-right: $spacing-unit;
}

.drawer-ng {
  padding-left: $spacing-unit * 2;
  border-left: 1px solid $text-color;
}

.drawer-post-publish-date {
  color: $sub-text-color;
}

.drawer-stream-item {
  padding: 16px 0;
  border-bottom: 1px solid $border-color;

  &:first-child {
    border-top: 1px solid $border-color;
  }
}

.drawer-stream-empty {
  color: $sub-text-color;
  text-align: center;
}

.drawer-twitter-applicant-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  gap: $spacing-unit;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: $spacing-unit * 2 $spacing-unit * 2 $spacing-unit * 4;
  text-align: center;
  background: white;
}

.drawer-current-page {
  color: $primary-color;
}

:deep(.el-tabs__item) {
  padding: 0 $spacing-unit;
}
</style>
